"use client";
import Image from "next/image";
import { Currency, CurrencyWithTax } from "@/components/ui/currency";
import { forwardRef, useEffect } from "react";
import * as motion from "motion/react-client";
import { cn } from "@/lib/utils";
import Link from "next/link";
import { generateProductURL } from "@/lib/shop/generate-url";
import { useLocalStorage } from "@mantine/hooks";
import { wait } from "@/lib/util/wait";
import { AddToCartButton } from "./add-to-cart-button";

const variations = {
  hidden: { opacity: 0, y: 18, scale: 0.97 },
  show: { opacity: 1, y: 0, scale: 0.97 },
};

type ProductOption = {
  id: string;
  name: string;
  values: {
    id: string;
    value: string;
    label: string;
  }[];
};

type ProductCardProps = {
  id: string;
  scrollHint?: boolean;
  images?: (string | undefined)[];
  title?: string;
  price?: number;
  strikePrice?: number;
  description?: string;
  vat?: number;
  variants?: {
    id: string;
    isDefault?: boolean;
    isAvailable?: number;
    price?: number;
    strikePrice?: number;
    vat?: number;
    optionValues: {
      id: string;
      label: string;
      value: string;
      name: string;
      optionId: string;
    }[];
  }[];
  options?: ProductOption[];
  className?: string;
};
export const ProductCard = forwardRef<HTMLDivElement, ProductCardProps>(
  (
    {
      id,
      images,
      title,
      scrollHint = false,
      price,
      vat = 0,
      strikePrice,
      description,
      options,
      variants,
      className = "",
    },
    ref,
  ) => {
    const hasMultipleVariants = variants && variants.length > 1;

    const defaultVariant = variants?.find((d) => d.isDefault);

    const defaultSelection: { option: string, value: string}[] = defaultVariant?.optionValues.map((d) => ({
      option: d.name,
      value: d.value,
    })) ?? [];

    return (
      <motion.article
        variants={variations}
        className={cn(
          "shrink-0 select-none rounded-2xl border bg-white",
          className,
        )}
      >
        <Link
          href={generateProductURL({ title: title ?? "", id })}
          className="flex h-full flex-col items-stretch p-4"
        >
          <figure className="flex h-72 w-full max-w-full snap-x snap-mandatory flex-row gap-0 overflow-x-auto">
            {images && images[0] && (
              <Gallery
                images={images as string[]}
                title={title ?? "Produktfoto"}
                scrollHint={scrollHint}
              />
            )}
          </figure>
          <figcaption className="mt-4 grow-0 break-all font-bold">
            {title}
          </figcaption>
          <p className="min-h-[1px] shrink-0 grow text-sm text-muted-foreground">
            {description}
          </p>

          {hasMultipleVariants && <VariantHint variants={variants} />}
          <div className="flex w-full flex-col items-start gap-2 self-start justify-self-end">
            <div className="mt-4 flex w-full shrink-0 justify-between">
              <div className="flex items-start self-start">
                <PriceDisplay
                  price={price!}
                  vat={vat}
                  strikePrice={strikePrice}
                />
              </div>
              <div
                className="self-end"
                onClick={(e) => {
                  e.stopPropagation();
                  e.preventDefault();
                }}
              >
                <AddToCartButton
                  id={id}
                  options={options ?? []}
                  variants={variants ?? []}
                  selected={defaultSelection}
                />
              </div>
            </div>
          </div>
        </Link>
      </motion.article>
    );
  },
);

const VariantHint = ({ variants }: { variants: any[] }) => {
  return (
    <div className="my-1 select-none rounded-xl border border-primary-200/50 bg-primary-50/30 px-2 py-0.5 text-center text-xs text-primary-500">
      +{variants.length - 1} weitere Variant
      {variants.length > 2 ? "en" : "e"}
    </div>
  );
};

const Gallery = ({
  images,
  title,
  scrollHint,
}: {
  images: string[];
  title: string;
  scrollHint: boolean;
}) => {
  const shouldScrollHint = scrollHint && images && images.length > 1;
  const showScrollHint = useScrollHinting(!!shouldScrollHint);

  return (
    <>
      {images.filter(Boolean).map((image, index) => (
        <motion.div
          className="relative h-full w-full shrink-0 grow snap-x snap-start"
          key={index}
        >
          <motion.div
            initial={{
              marginLeft: showScrollHint ? 0 : 0,
            }}
            animate={{
              marginLeft: showScrollHint ? -250 : 0,
            }}
            transition={scrollHintTransition}
            className="relative h-full w-full"
          >
            <Image
              src={image!}
              alt={title ?? "Product"}
              fill
              className="object-contain"
              sizes="400px"
            />
          </motion.div>
        </motion.div>
      ))}
    </>
  );
};

const scrollHintTransition = {
  delay: 0.3,
  repeat: 1,
  repeatType: "mirror" as const,
  type: "spring" as const,
  bounce: 0.25,
};

const useScrollHinting = (show: boolean) => {
  const [hasSeenScrollHint, setHasSeenScrollHint] = useLocalStorage({
    key: "pcards-sch",
    defaultValue: false,
  });

  const showScrollHint = show && !hasSeenScrollHint;

  useEffect(() => {
    if (showScrollHint && !hasSeenScrollHint) {
      wait(2000).then(() => {
        setHasSeenScrollHint(true);
      });
    }
  }, [showScrollHint, hasSeenScrollHint, setHasSeenScrollHint]);

  return showScrollHint;
};

const PriceDisplay = ({
  price,
  strikePrice,
  vat,
}: {
  price: number;
  strikePrice?: number;
  vat?: number;
}) => {
  return (
    <div className="mt-4 flex flex-row items-start justify-start gap-1">
      <span
        className={cn(
          strikePrice ? "text-orange-600" : "text-gray-900",
          !price ? "font-light text-muted-foreground" : "font-bold",
        )}
      >
        {!!price && <CurrencyWithTax amount={price} taxRate={vat} />}
        {!price && "auf Anfrage"}
      </span>
      {!!strikePrice && (
        <span className="relative bottom-1 text-sm text-gray-400 line-through">
          <CurrencyWithTax amount={strikePrice} taxRate={vat} />
        </span>
      )}
    </div>
  );
};

ProductCard.displayName = "ProductCard";
